import { getProperty as getProp } from 'dot-prop'
import { asAutoplay } from '~/lib/autoplayWrites'
import { CONTEXTS, IS_LOCAL, SESSION_STATUS } from '~/lib/constants'
import { navigateTo } from '~/lib/navigate'
import paramChannel from '~/lib/paramChannel'
import { carriedObjects, withoutPinned } from '~/lib/livePins'
import { migrateSettings } from '~/lib/migrations'
import { parseLivePath, schemaForLivePath } from '~/lib/params'
import { pinValue } from '~/lib/pinLayer'
import { EFFECT_SCHEMAS, loadableSettings, stripAdvancedFromSettings, stripSettings } from '~/lib/presetSettings'
import { stageIncoming } from '~/lib/sources/fillStage'
import socket from '~/lib/socket'
import transitionManager from '~/lib/transitionManager'
import { planRun } from './planRun'
import { derivePresetName, generateName } from '~/lib/utils'
import api from '~/store/api'
import { selectSessionStatus } from '~/store/api/selectors'
import { selectIsRunner, selectProjectData, selectProjectId } from '~/store/app/selectors'
import { selectLive } from '~/store/live/selectors'
import { registerAction } from '~/store/middleware/confirmActions'
import { clearSelection, setSelection } from '~/store/selection'
import { selectIds } from '~/store/selection/selectors'
import { SCRUB_END_EPSILON } from '~/lib/constants'
import { setSettingsProperty } from '~/store/settings'
import { selectSettings } from '~/store/settings/selectors'
import { requestConfirm } from '~/store/ui'
import { selectTexturesReady } from '~/store/ui/selectors'
import {
	setCurrentPresetIndex,
	setNextPresetIndex,
	setPlaying,
	setTransitionDetached,
	setTransitionReversed,
	clearMorph,
	presetLoaded,
} from './'
import {
	selectSavePresetIndex,
	selectCurrentPresetIndex,
	selectLiveInvalid,
	selectExamplePresets,
	selectPlayback,
	selectPlaying,
	selectPresetChanged,
	selectPlaylist,
	selectPresets,
	selectLiveProps,
	touchesFadingPreset,
} from './selectors'

const logger = console.scope('playback-thunks')

// A playlist entry past the presets is an example: selected under its own focus, so delete and cut never reach it.
const selectEntry = (dispatch, getState, preset) =>
	dispatch(
		setSelection({
			focus: selectPresets(getState()).includes(preset) ? 'preset' : CONTEXTS.EXAMPLE_PRESET,
			ids: [preset.id],
		})
	)

// Seconds a drag-started fade spans when `fade_duration` is 0. `t` is a fraction of the timeline's length, so a zero-length one has no interior positions to drag through — this is the scale the drag maps onto, never a wait the user sits through.
const SCRUB_DURATION = 1

// These live in ~/lib/presetSettings — pure functions over the settings shape, importable without the store graph. Re-exported here for existing callers.
export { stripAdvancedFromSettings, stripSettings }

// The pinned values as `[[key, value], ...]`, for `setLiveObject`'s `meta.pins`.
const buildLivePropRestorePayload = state => {
	const liveProps = selectLiveProps(state)
	if (!liveProps.length) return []
	return (
		liveProps
			// An existence pin has no value to restore — the carry brings the object itself.
			.filter(key => parseLivePath(key)?.existence !== true)
			.map(key => {
				// ⚠️ Before a project's first load, live and the channel are the previous project's: the layer alone. After it, channel first: a mid-fade pin froze there.
				const val = !state.playback.pins_loaded
					? pinValue(state.playback.pins, key)
					: paramChannel.has(key)
						? paramChannel.get(key)
						: getProp(state.live, key)
				if (val !== undefined) return [key, val]
				// ⚠️ Absent is at-initial (the write rule deletes it), and that IS the pinned value: skipped, the incoming preset's value lands.
				const initial = schemaForLivePath(key, state.live?.objects)?.initial
				return initial !== undefined ? [key, initial] : null
			})
			.filter(Boolean)
	)
}

// Before the project's first load, live is the previous project's: the layer alone places each pinned object.
const carriedFromLive = (state, incoming) => {
	const { pins, pins_loaded } = state.playback
	return carriedObjects(pins_loaded ? state.live?.objects : null, selectLiveProps(state), incoming, pins)
}

// What a save writes: live without its pinned values.
const presetSettingsOf = state =>
	stripSettings(withoutPinned(stripAdvancedFromSettings(selectLive(state)), selectLiveProps(state)))

/**
 * THE SETTLE: a fast gesture writes only the channel, and whatever ENDS it lands the result in Redux — what `selectPresetChanged` diffs and what a save writes.
 *
 * ⚠️ A `patch` comes from the manager's DENSE bags, never the channel: absence there means at-initial, and an absent key in a patch means "leave alone", so a param swept back to its initial would stay stale.
 */
export const settleLive =
	({ settings, patch, skipSync = false }) =>
	(dispatch, getState) => {
		if (settings) return dispatchLivePreset(dispatch, getState, settings, { skipSync })
		// The scene graph settles through the fade's own kill/handoff path, not through a patch.
		// eslint-disable-next-line no-unused-vars
		const { objects: unused, ...effects } = patch ?? {}
		if (!Object.keys(effects).length) return
		// The gesture already bracketed its own history; the mid-flight values were never history either.
		dispatch({ type: 'live/patchLiveObject', payload: effects, meta: { skipHistory: true } })
	}

/**
 * Settle a fade parked between its ends — the one terminal state that fires no callback.
 *
 * ⚠️ `omitMorphs`, and no `holdMorphables`. The park leaves the fade LIVE with its timeline paused, so nothing republishes: a morph key named here would put the destination scalar on the wire and cut the image while the bar still reads 25%. Omitted, the envelope survives — it lives in the CHANNEL, which `withChannelParams` is what the wire reads, and Redux governs only the key's EXISTENCE, which `patchLiveObject` leaves alone for a key the patch does not name. `abortTransition` needs `holdMorphables` for the same job because it re-asserts DENSE settings through `setLiveObject`, replacing `live` whole.
 */
export const commitLiveFromTransition = () => dispatch =>
	dispatch(settleLive({ patch: transitionManager.getActiveState({ omitMorphs: true }) ?? {} }))

// A media session is live (streaming) — controls whether preset loads use skipSync.
const isSessionActive = state => {
	const status = selectSessionStatus(state)
	return status === SESSION_STATUS.STARTED || status === SESSION_STATUS.RUNNING
}

// Load preset settings into live state. `skipSync` suppresses the broadcast to the server; it is ONLY correct for a rejoin-time load (loadFirstPreset/loadLastPreset on startup with a session already running), where the server holds authoritative state and pushes it back over the data channel. A user-initiated load has no such push behind it, so it must sync like any other live mutation — otherwise the render keeps the old params and liveSync's baseline drifts.
const dispatchLivePreset = (dispatch, getState, settings, { skipSync = false } = {}) => {
	const restorePayload = buildLivePropRestorePayload(getState())
	// Migrate, do NOT expand: `loadPresetSettings` also serves dev tools and pasted settings, and the chain is idempotent.
	const clean = migrateSettings(stripAdvancedFromSettings(settings))
	// `skipSync` suppresses the broadcast, never the carry.
	const carryObjects = carriedFromLive(getState(), clean.objects)
	stageIncoming(clean)
	dispatch({
		type: 'live/setLiveObject',
		payload: clean,
		meta: { carryObjects, pins: restorePayload, ...(skipSync && { skipSync: true }) },
	})
}

// Load arbitrary preset settings into live as a dirty (unsaved) change, without changing current_preset_index or starting a transition. Used by the example-presets panel and dev tools — the same `dispatchLivePreset` path every normal preset load uses, so example presets behave exactly like user presets except for provenance.
export const loadPresetSettings = settings => dispatch => {
	dispatch(settleLive({ settings }))
}

export const loadFirstPreset = () => (dispatch, getState) => {
	const s = getState()
	const data = selectProjectData(s)
	if (!data) {
		logger.warn('loadFirstPreset', 'No project data found')
		return
	}

	const { project, presets } = data

	// Rejoining a running session: the server's state wins, don't push the preset at it.
	const skipSync = isSessionActive(s)

	// Load first preset
	if (project?.presets?.length && presets?.length) {
		const presetID = project.presets[0]
		const preset = presets.find(p => p.id === presetID)
		if (preset) {
			dispatch(settleLive({ settings: preset.settings, skipSync }))
			dispatch(setCurrentPresetIndex(0))
			dispatch(setNextPresetIndex(-1))
			dispatch(setSelection({ focus: 'preset', ids: [presetID] }))
		} else {
			logger.warn('loadFirstPreset', `Preset with ID ${presetID} not found`)
		}
	} else {
		logger.warn('loadFirstPreset', 'No presets found')
	}
}

export const loadLastPreset = () => (dispatch, getState) => {
	const s = getState()
	const data = selectProjectData(s)
	if (!data) {
		logger.warn('loadLastPreset', 'No project data found')
		return
	}

	const { project, presets } = data
	if (!project?.presets?.length || !presets?.length) {
		logger.warn('loadLastPreset', 'No presets found')
		return
	}

	// Rejoining a running session: the server's state wins, don't push the preset at it.
	const skipSync = isSessionActive(s)

	// Get the persisted index from state
	const persistedIndex = s.playback.current_preset_index

	// Check if persisted index points to a template preset (unified index >= user presets count)
	const userPresetsCount = Math.min(project.presets.length, presets.length)
	if (persistedIndex >= userPresetsCount) {
		const templatePresets = selectExamplePresets(s)
		const templateIndex = persistedIndex - userPresetsCount
		if (templateIndex >= 0 && templateIndex < templatePresets.length) {
			logger.info('loadLastPreset', `Loading template preset at index ${templateIndex}`)
			dispatch(loadTemplatePreset(templateIndex, { skipSync }))
			return
		}
		// Template index out of bounds, fall through to load user preset
	}

	// Validate persisted index, fall back to 0 if invalid Check against both the project.presets order array and the presets data array
	const maxIndex = userPresetsCount - 1
	const targetIndex = persistedIndex >= 0 && persistedIndex <= maxIndex ? persistedIndex : 0

	const presetID = project.presets[targetIndex]
	const preset = presets.find(p => p.id === presetID)

	if (preset) {
		logger.info('loadLastPreset', `Loading preset at index ${targetIndex}`)
		dispatch(settleLive({ settings: preset.settings, skipSync }))
		dispatch(setCurrentPresetIndex(targetIndex))
		dispatch(setNextPresetIndex(-1))
		dispatch(setSelection({ focus: 'preset', ids: [presetID] }))
	} else {
		logger.warn('loadLastPreset', `Preset with ID ${presetID} not found, falling back to first`)
		dispatch(loadFirstPreset())
	}
}

// Template preset thunks (for mobile app)

export const loadTemplatePreset =
	(templateIndex, { skipSync = false } = {}) =>
	(dispatch, getState) => {
		const s = getState()
		const templatePresets = selectExamplePresets(s)
		const userPresets = selectPresets(s)

		if (templateIndex < 0 || templateIndex >= templatePresets.length) {
			logger.warn('loadTemplatePreset', `Template preset index ${templateIndex} out of bounds`)
			return
		}

		const preset = templatePresets[templateIndex]
		logger.info('loadTemplatePreset', { templateIndex, name: preset.name })

		// Unified index: template presets start after user presets
		const unifiedIndex = userPresets.length + templateIndex

		dispatch(setNextPresetIndex(-1))
		dispatch(setCurrentPresetIndex(unifiedIndex))
		dispatch(clearSelection())

		dispatch(settleLive({ settings: preset.settings, skipSync }))
	}

export const loadFirstTemplatePreset = () => (dispatch, getState) => {
	const templatePresets = selectExamplePresets(getState())
	if (templatePresets.length > 0) {
		dispatch(loadTemplatePreset(0))
	} else {
		logger.warn('loadFirstTemplatePreset', 'No template presets available')
	}
}

/** Cue a preset. Returns whether a FADE opened — a hard cut is `false`. ⚠️ Synchronous, and callers rely on it: `next_preset_index` reads back in the same turn. Nothing here may `await`. */
export const loadPreset =
	({
		index,
		instant = false,
		sync = false,
		confirmed = false,
		force = false,
		select = false,
		advance = false,
		scrub = false,
	}) =>
	(dispatch, getState) => {
		const s = getState()
		if (!confirmed && selectSettings(s).confirm_unsaved_preset) {
			const presetChanged = selectPresetChanged(s)
			if (presetChanged) {
				dispatch(
					requestConfirm({
						id: 'unsaved_preset',
						title: 'Unsaved Preset',
						message: 'You have unsaved changes. Do you want to continue?',
						// ⚠️ EVERY flag rides through, not just the three the modal reads. `advance` is load-bearing: a confirmed re-entry without it reads as a USER load, which calls `stopPlayback()` — autoplay dies on its own advance.
						action: {
							type: 'loadPreset',
							params: { index, instant, sync, confirmed: true, force, select, advance, scrub },
						},
						confirmType: 'confirm_unsaved_preset',
					})
				)
				return false
			}
		}
		const playback = selectPlayback(s)
		if (sync && playback.playing) return false
		const presets = selectPlaylist(s)
		if (index < 0 || index >= presets.length) return false
		const { current_preset_index, instant: global_instant, fade_duration } = playback

		// Capture mid-tween state before killing any active transition This is used as fromState so interruption starts from the visual midpoint, not Redux state
		//
		// ⚠️ The manager, not `next_preset_index`. The flag is Redux's record of a fade being ANNOUNCED, and a run sets it per fade child — but a hold child leaves it at -1 while a plan is very much running, and a shipped fade could strand it at a real index with no timeline behind it. `isActive()` asks the only authority: is a fade child at the playhead, still travelling.
		const transitioning = transitionManager.isActive()
		// How far the fade we are about to replace had actually travelled. Read BEFORE createTransition kills it — afterwards the timeline is gone.
		const outgoingProgress = transitioning ? (transitionManager.timeline?.progress() ?? 0) : 0
		let fromState
		if (transitioning) {
			// Null only if the playhead is not on a fade, which `transitioning` already excludes; the fallback keeps this honest rather than trusting two reads to agree.
			fromState = transitionManager.getActiveState() ?? s.live
			// No kill() here: createTransition below resolves the outgoing transition's objects against its own destination, and the instant path (else-branch) resolves against Redux. Killing here with no successor in hand is what stranded half-faded objects on the stage — see context/transitions.md §Object ownership.
			socket.flushDCLog()
			// Do NOT update current_preset_index — it correctly stays at the origin preset next_preset_index will be overwritten below when the new transition starts
		} else {
			fromState = s.live
		}

		const oldIndex = current_preset_index
		// Allow re-targeting the origin preset while transitioning (reversal)
		if (index === oldIndex && !force && !transitioning) return false

		logger.info(`loadPreset: ${oldIndex} > ${index}`)

		const preset = presets[index]
		if (!preset) {
			logger.error(`loadPreset: not found`, { index, presets })
			return false
		}

		const settings = loadableSettings(preset.settings)

		// A scrub is a fade the user drives by hand, so it takes the animated path whatever `fade_duration` says — a hard cut has no progress to drag.
		const fading =
			oldIndex >= 0 && selectTexturesReady(s) && (scrub || (!instant && !global_instant && fade_duration))

		// ⚠️ Residency is a precondition, and refusing must precede the teardown below — a refusal that already stopped autoplay kills the playlist for a load that never happened.
		if (fading && transitionManager.preloadTextures(settings) !== true) return false

		// A user-initiated load takes the playlist over: autoplay stops. `advance` marks the playlist's OWN dispatches, the only loads that are not the user choosing a preset.
		//
		// ⚠️ PARK the run first, and only then stop it. `stopPlayback` drops the plan through `killRun`, which for a fade at the playhead is a `kill()` — tearing down objects the interrupt about to be built is going to keep animating. Parking settles that child handing its ownership over, exactly as a boundary does, so `createTransition` below adopts it. On anything but a run this is a no-op.
		if (!advance && playback.playing) {
			transitionManager.parkRun()
			dispatch(stopPlayback())
		}

		if (fading) {
			// Start transition
			dispatch(setNextPresetIndex(index))
			// Detach when the source is a mid-fade snapshot no preset represents: `current_preset_index` still names the ORIGINAL origin, which is not where this fade starts, so nothing may be drawn against that cell.
			//
			// Gated on the outgoing fade having actually MOVED, not merely existing. A fade still sitting at 0 — a scrub parked under the pointer, or one interrupted on its first frame — has not left the origin, so the origin is still the honest source. Reversing to that origin is the other exception: there the source really is that preset. Must follow setNextPresetIndex, which clears the flag.
			if (transitioning && index !== oldIndex && outgoingProgress > 0) dispatch(setTransitionDetached(true))

			// ⚠️ At fade START: the destination's streams must be acquiring while the fade runs.
			stageIncoming(settings)

			// A scrubbed fade still needs a real duration to interpolate over even when `fade_duration` is 0: `t` is a fraction of it, and a zero-length timeline has no positions between its ends.
			const tl = transitionManager.createTransition(
				fromState,
				settings,
				scrub ? fade_duration || SCRUB_DURATION : fade_duration,
				selectLiveProps(s)
			)
			// Park it under the user's finger. Nothing advances until they drag.
			if (scrub) transitionManager.seek(0)

			// When transition completes
			tl.eventCallback('onComplete', () => {
				socket.flushDCLog()
				dispatch(setNextPresetIndex(-1))
				dispatch(setCurrentPresetIndex(index))
				if (select) selectEntry(dispatch, getState, preset)
				dispatch(settleLive({ settings }))
				// ⚠️ Only AFTER `setLiveObject` snaps each morphable param to its plain destination, or a field derives `plain` mode against an envelope and flickers.
				//
				// A STANDALONE fade hands over to nothing, so its morphs go. The run's boundary is the case that hands over, and it never renders between the two children — which is what removes the collapse this used to need.
				if (Object.keys(getState().playback.morphs).length) dispatch(clearMorph())
				// ⚠️ This fade was NOT part of a plan, so nothing else will re-plan behind it. Without this, autoplay left on over a manual fade stays on and never advances again once the fade lands.
				startRun(dispatch, getState)
			})
			dispatch(presetLoaded({ index, instant, sync, advance }))
			return true
		} else {
			// Direct switch. If a transition was running, resolve its objects against the preset we are snapping to — otherwise anything it created stays on the stage forever, unreachable (Redux never knew it). This is the instant-interrupt ghost.
			if (transitioning) transitionManager.kill(settings?.objects ?? null)
			dispatch(setNextPresetIndex(-1))
			dispatch(setCurrentPresetIndex(index))
			if (select) selectEntry(dispatch, getState, preset)
			const restorePayload = buildLivePropRestorePayload(getState())
			const carryObjects = carriedFromLive(getState(), settings?.objects)
			stageIncoming(settings)
			dispatch({ type: 'live/setLiveObject', payload: settings, meta: { carryObjects, pins: restorePayload } })
			// Re-plan only outside a run: a zero-fade `cut` fires from a hold's `onStart`, where this would kill the parent mid-tick.
			if (advance && !transitionManager.isRun()) startRun(dispatch, getState)
			dispatch(presetLoaded({ index, instant, sync, advance }))
			return false
		}
	}

// Dev only — the rig (tests/manual/rig.js) drives the REAL thunk; replicating its interrupt sequence would test the copy rather than the shipped path. Never in production: any page script could drive preset loads.
if (IS_LOCAL && typeof window !== 'undefined') window.loadPreset = loadPreset

// API

export const addPreset = payload => async (dispatch, getState) => {
	const s = getState()
	if (s.app.is_template) {
		navigateTo('/save_template_as')
		return
	}
	const presets = selectPresets(s)

	// Enforce 100 preset limit
	const PRESET_LIMIT = 100
	if (presets.filter(Boolean).length >= PRESET_LIMIT) {
		logger.warn('addPreset: Preset limit reached (100)')
		return
	}

	const { current_preset_index } = selectPlayback(s)

	let orderIndex = -1
	let name = payload?.name

	// If orderIndex is explicitly provided, use it (e.g., from PresetStrip add button)
	if (payload?.orderIndex !== undefined) {
		orderIndex = payload.orderIndex
	} else if (current_preset_index >= 0 && presets.length > 0) {
		// Add after the current (loaded) preset; an example on stage is past the presets, so its copy goes last.
		orderIndex = Math.min(current_preset_index + 1, presets.length)
	} else {
		// Or at the end
		orderIndex = presets.length
	}

	if (!name) {
		const live = selectLive(s)
		name = derivePresetName(presets, current_preset_index, live.diffusion?.prompt)
	} else {
		name = generateName(presets, 'name', name, 'Preset ')
	}

	logger.info('addPreset:', { name, orderIndex, current_preset_index })

	const settings = presetSettingsOf(s)

	// Use passed thumbnail or capture from output video, PixiJS canvas, or camera video
	let thumbnail = payload?.thumbnail || null
	const thumbnailUrl = payload?.thumbnailUrl || null
	if (!thumbnail && window.output_video?.videoWidth > 0) {
		try {
			const { captureVideoThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
			thumbnail = await captureVideoThumbnail(window.output_video)
		} catch {
			// Ignore - capture failed or module not available
		}
	}
	if (!thumbnail && window.pixi_app?.canvas) {
		try {
			const { captureCanvasThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
			thumbnail = await captureCanvasThumbnail(window.pixi_app.canvas)
		} catch {
			// Ignore - capture failed or module not available
		}
	}
	if (!thumbnail && window.camera_video?.videoWidth > 0) {
		try {
			const { captureVideoThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
			thumbnail = await captureVideoThumbnail(window.camera_video)
		} catch {
			// Ignore - capture failed or module not available
		}
	}

	return dispatch(
		api.endpoints.addPreset.initiate({
			projectId: s.app.project_id,
			name,
			orderIndex,
			settings,
			thumbnail,
			thumbnailUrl,
		})
	)
}

export const savePreset =
	(targetPresetId = null) =>
	async (dispatch, getState) => {
		const s = getState()
		if (s.app.is_template) {
			navigateTo('/save_template_as')
			return
		}
		// ⚠️ The gate belongs HERE, not only in `addOrSavePreset`: a caller passing an explicit id makes `selectSavePresetIndex`'s `-1` inert, and every save button does.
		if (selectLiveInvalid(s)) {
			logger.warn('savePreset: refusing to save a running morph')
			return
		}
		const presets = selectPresets(s)
		const current_preset_index = selectSavePresetIndex(s)
		const project_id = selectProjectId(s)

		// Use provided presetId or fall back to current preset
		const presetId = targetPresetId ?? presets[current_preset_index]?.id
		if (!presetId) {
			logger.warn('savePreset: no preset to save')
			return
		}

		if (typeof presetId !== 'string') {
			logger.warn('savePreset: presetId is not a string', presets, current_preset_index)
			return
		}

		// Rewriting the settings a fade is interpolating from or toward corrupts it exactly as deleting the preset would.
		if (touchesFadingPreset(s, presetId)) {
			logger.warn('savePreset: refused, the preset is in the running fade')
			return
		}

		logger.info('savePreset:', { presetId, current_preset_index })

		// Capture thumbnail from output video, PixiJS canvas, or camera video
		let thumbnail = null
		if (window.output_video?.videoWidth > 0) {
			try {
				const { captureVideoThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
				thumbnail = await captureVideoThumbnail(window.output_video)
			} catch {
				// Ignore - capture failed or module not available
			}
		}
		if (!thumbnail && window.pixi_app?.canvas) {
			try {
				const { captureCanvasThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
				thumbnail = await captureCanvasThumbnail(window.pixi_app.canvas)
			} catch {
				// Ignore - capture failed or module not available
			}
		}
		if (!thumbnail && window.camera_video?.videoWidth > 0) {
			try {
				const { captureVideoThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
				thumbnail = await captureVideoThumbnail(window.camera_video)
			} catch {
				// Ignore - capture failed or module not available
			}
		}

		// Save thumbnail locally for immediate display (server upload happens in parallel)
		if (thumbnail) {
			import('~/routes/mobile/cache/presetThumbnailCache')
				.then(({ setThumbnail }) => setThumbnail(presetId, project_id, thumbnail))
				.catch(err => logger.warn('Failed to cache thumbnail locally:', err))
		}

		dispatch(
			api.endpoints.updatePreset.initiate({
				projectId: project_id,
				id: presetId,
				settings: presetSettingsOf(s),
				thumbnail,
			})
		)
	}

export const recaptureThumbnail = payload => async (dispatch, getState) => {
	const s = getState()
	const project_id = selectProjectId(s)
	const presetId = payload?.id || selectIds(s)[0]
	if (!presetId || !project_id) return

	let thumbnail = null
	if (window.output_video?.videoWidth > 0) {
		try {
			const { captureVideoThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
			thumbnail = await captureVideoThumbnail(window.output_video)
		} catch {
			// Ignore
		}
	}
	if (!thumbnail && window.pixi_app?.canvas) {
		try {
			const { captureCanvasThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
			thumbnail = await captureCanvasThumbnail(window.pixi_app.canvas)
		} catch {
			// Ignore
		}
	}
	if (!thumbnail && window.camera_video?.videoWidth > 0) {
		try {
			const { captureVideoThumbnail } = await import('~/routes/mobile/cache/presetThumbnailCache')
			thumbnail = await captureVideoThumbnail(window.camera_video)
		} catch {
			// Ignore
		}
	}

	if (!thumbnail) throw new Error('Failed to capture thumbnail — no video or canvas source available')

	import('~/routes/mobile/cache/presetThumbnailCache')
		.then(({ setThumbnail }) => setThumbnail(presetId, project_id, thumbnail))
		.catch(err => logger.warn('Failed to cache thumbnail locally:', err))

	return dispatch(
		api.endpoints.updatePreset.initiate({
			projectId: project_id,
			id: presetId,
			thumbnail,
		})
	)
}

// The current index may name an example or template cell past the user presets; only a user preset is a save target, and a morph on one refuses rather than duplicating its stale pre-morph values.
export const addOrSavePreset = () => (dispatch, getState) => {
	const s = getState()
	if (!selectPresets(s)[selectCurrentPresetIndex(s)]) dispatch(addPreset())
	else if (!selectLiveInvalid(s)) dispatch(savePreset())
}

export const reorderPresets =
	({ oldIndex, newIndex }) =>
	(dispatch, getState) => {
		const s = getState()
		const presets = selectPresets(s)
		const projectId = selectProjectId(s)

		if (oldIndex === newIndex || !presets?.length) return
		if (oldIndex < 0 || oldIndex >= presets.length) return
		if (newIndex < 0 || newIndex >= presets.length) return

		// The whole span between the two positions is renumbered, so a fade endpoint anywhere inside it is affected — not only the preset being dragged.
		const lo = Math.min(oldIndex, newIndex)
		const hi = Math.max(oldIndex, newIndex)
		if (
			touchesFadingPreset(
				s,
				presets.slice(lo, hi + 1).map(p => p?.id)
			)
		) {
			logger.warn('reorderPresets: refused, a preset in the running fade would be renumbered')
			return
		}

		// Create new order by moving preset from oldIndex to newIndex
		const newOrder = [...presets]
		const [moved] = newOrder.splice(oldIndex, 1)
		newOrder.splice(newIndex, 0, moved)

		// Extract preset IDs for the API call
		const presetIds = newOrder.map(p => p.id)

		logger.info('reorderPresets:', { oldIndex, newIndex, presetIds })

		dispatch(api.endpoints.updateProject.initiate({ id: projectId, presets: presetIds }))
	}

/** Run the playlist on into the desktop examples, or stop at the last preset. Saved on the project. */
export const toggleExamplesInPlaylist = () => (dispatch, getState) => {
	const project = selectProjectData(getState())?.project
	if (!project) return
	const settings = project.settings ?? {}
	// ⚠️ The WHOLE settings: the server replaces the field, so a partial one would drop the thumbnail.
	dispatch(
		api.endpoints.updateProject.initiate({
			id: project.id,
			settings: { ...settings, playlist_examples: !settings.playlist_examples },
		})
	)
}

/** Hide the Examples folder on this machine (Settings → General brings it back); `selectPlaylist` skips them here, the project's flag untouched. */
export const hideExamples = () => dispatch => {
	dispatch(setSettingsProperty(['show_example_presets', false]))
}

// Playback

/**
 * The autoplay run: one GSAP parent timeline whose children are the fades and holds.
 *
 * The clock lives in `transitionManager`; this owns the SCENE work each child does at its own `onStart`. There is no second clock and no window between them — that window was the defect three shipped patches each guessed differently inside of.
 */

// Everything the run needs to rebuild itself, or null when nothing is planned.
let run = null

/** A playing session this runner could not arm while textures warmed. A press while warming is refused outright. */
let awaitingWarm = false

/** Drop the run entirely — project switch, `stopPlayback`, or anything that invalidates the plan. */
const dropRun = dispatch => {
	run = null
	// ⚠️ A fade inside the run dies WITH it, settling nothing through a timeline, so the flag it set has no other route back to -1 — leaving `selectIsTransitioning` true at rest.
	//
	// ⚠️ NOT after a park. Parking hands the fade's ownership to the interrupt `loadPreset` is about to open, and that interrupt sets the flag to its own destination in the very next statement — clearing here would blank the fade the user just asked for. `isRun()` is already false by then, which is what makes this read correctly rather than by luck.
	if (transitionManager.isRun() && transitionManager.isLive()) dispatch(setNextPresetIndex(-1))
	transitionManager.killRun()
}

/**
 * The scene work for ONE fade child, at its own `onStart`.
 *
 * ⚠️ No dispatch may land a reentrant `loadPreset` between the outgoing child's settle and this build: it would read `getActiveState()` on nulled registries and snapshot destination values — the original pop.
 */
const buildFadeChild = (dispatch, getState, step, child) => {
	const s = getState()
	const preset = selectPlaylist(s)[step.to]
	if (!preset) return

	const settings = loadableSettings(preset.settings)

	dispatch(setNextPresetIndex(step.to))
	// At fade START, as `loadPreset` does: the live crossfade needs the destination acquiring while it runs.
	stageIncoming(settings)
	// ⚠️ Never awaited: `warmTextures` has already made every cueable image resident, and an await here would let a dispatch land inside the settle-to-build window.
	transitionManager.preloadTextures(settings)
	transitionManager.buildFade(child, selectLive(s), settings, step.ms / 1000, selectLiveProps(s))
}

/** A fade child landed: Redux catches up to where the timeline already is. */
const settleFadeChild = (dispatch, getState, step, child, handingOver) => {
	socket.flushDCLog()
	const s = getState()
	const preset = selectPlaylist(s)[step.to]
	if (!preset) return
	const settings = loadableSettings(preset.settings)
	dispatch(setNextPresetIndex(-1))
	dispatch(setCurrentPresetIndex(step.to))
	dispatch(presetLoaded({ index: step.to, instant: false, advance: true }))
	dispatch(settleLive({ settings }))
	const landed = transitionManager.takeLandedEdits(child)
	if (landed.length) dispatch({ type: 'live/setLiveProperties', payload: landed, meta: { skipHistory: true } })
	// ⚠️ Only AFTER `settleLive` snaps each morphable param to its plain destination, or a field derives `plain` mode against an envelope and flickers.
	//
	// A successor fade opening in this same tick re-arms them, so they stay. Anything else — a hold, the end of the run — leaves nothing to interpolate, and a surviving entry blocks saving through `selectLiveInvalid`.
	if (!handingOver && Object.keys(getState().playback.morphs ?? {}).length) dispatch(clearMorph())
}

/**
 * A hold child began.
 *
 * ⚠️ `step.cut` carries a zero-`fade_duration` advance, which has no span and so no child of its own to land in.
 */
const beginHold = (dispatch, step) => {
	dispatch(setNextPresetIndex(-1))
	if (step.cut !== undefined) dispatch(loadPreset({ index: step.cut, advance: true, instant: true, confirmed: true }))
}

/**
 * Plan the run from the current state and start it.
 *
 * ⚠️ A fade already in flight is left exactly as it is. The plan is built BEHIND it and adopted when it lands — never rebuilt, never a second run.
 */
export const startRun = (dispatch, getState) => {
	const s = getState()
	if (!selectPlaying(s)) return
	const { steps, refused } = planRun(s)
	if (refused) {
		logger.warn('startRun refused', refused)
		dispatch(stopPlayback())
		return
	}
	run = { steps }
	transitionManager.startRun(steps, {
		onFade: asAutoplay((step, child) => buildFadeChild(dispatch, getState, step, child)),
		onHold: asAutoplay(step => beginHold(dispatch, step)),
		onSettle: asAutoplay((step, child, handingOver) =>
			settleFadeChild(dispatch, getState, step, child, handingOver)
		),
		onEnd: asAutoplay(() => {
			// One lap done. Re-plan from where it landed, or stop when `loop` is off.
			if (selectPlayback(getState()).loop) startRun(dispatch, getState)
			else dispatch(stopPlayback())
		}),
	})
}

/** Every project switch: a plan built against the old project's indices means nothing in the new one. */
export const dropPlan = () => dispatch => {
	awaitingWarm = false
	dropRun(dispatch)
}

/** Re-point both indices at the presets they named, by id, after the list moved under them. */
export const reindexPlayback = before => (dispatch, getState) => {
	const s = getState()
	const after = selectPlaylist(s)
	const { current_preset_index, next_preset_index } = selectPlayback(s)
	const find = i => {
		const id = before[i]?.id
		if (!id) return i
		const at = after.findIndex(p => p.id === id)
		// Its own preset was deleted: the slot is held rather than re-pointed, but never past the end — `planRun` does not range-check `from`.
		return at < 0 ? Math.min(i, after.length - 1) : at
	}
	const cur = find(current_preset_index)
	if (current_preset_index >= 0 && cur !== current_preset_index) dispatch(setCurrentPresetIndex(cur))
	const next = find(next_preset_index)
	if (next_preset_index >= 0 && next !== next_preset_index) dispatch(setNextPresetIndex(next))
}

/**
 * A duration, the preset order, or the preset list changed — from this client or a peer.
 *
 * ⚠️ NOT `startRun`: that kills the fade at the playhead and rebuilds it from 0, a visible jump mid-fade. A hand-driven or parked fade has no parent to re-plan, and its own ending re-plans through `startRun`.
 */
export const replan = () => (dispatch, getState) => {
	if (!run) return
	const { steps, refused } = planRun(getState())
	if (refused) {
		logger.warn('replan refused', refused)
		dispatch(stopPlayback())
		return
	}
	// Same route, different durations → a speed change the playhead absorbs in place. Anything else is a rebuild of what comes after the current child.
	const sameRoute =
		run.steps.length === steps.length &&
		run.steps.every(
			(s, i) =>
				s.kind === steps[i].kind && s.from === steps[i].from && s.to === steps[i].to && s.at === steps[i].at
		)
	run = { steps }
	if (!sameRoute || !transitionManager.retime(steps)) transitionManager.replanTail(steps)
}

// ── Transition control ────────────────────────────────────────────────────────────────────────
//
// The transition is a first-class object the user drives directly, independent of autoplay: these are what the transition bar dispatches.

/** Run the fade out from wherever it sits, settling it — see transitionManager.complete for why a fade resting at 1 needs this rather than a plain resume. */
export const completeTransition = () => () => {
	transitionManager.complete()
}

/**
 * Carry every morphable param's CURRENT value into a settings patch, in place.
 *
 * ⚠️ A snapshot names a morphing param by its DESTINATION, so landing on it jumps to a value the pipeline never reached; holding the live value keeps the server anchored where it is.
 *
 * ⚠️ ASSIGNED, never deleted: `setLiveObject` replaces `live` whole, so an omitted key reads as the contract initial and blanks the param.
 */
const holdMorphables = (settings, live) => {
	for (const effect in EFFECT_SCHEMAS) {
		const props = EFFECT_SCHEMAS[effect]?.properties
		for (const key in props) {
			if (!props[key].morph) continue
			// ⚠️ The CHANNEL wins where it holds the key: a fade's morph values reach Redux only at the settle, so reading `live` alone anchors the server at the pre-fade value.
			const qualified = `${effect}.${key}`
			const held = paramChannel.has(qualified) ? paramChannel.get(qualified) : live?.[effect]?.[key]
			if (held === undefined) delete settings[effect]?.[key]
			else (settings[effect] ??= {})[key] = held
		}
	}
}

/**
 * Abandon the fade at its origin: the user scrubbed all the way back, so the destination is not where they left off.
 *
 * `kill(toObjects)` resolves owned objects against whatever comes next, and what comes next here is the state we started from.
 *
 * ⚠️ `live` must be RE-ASSERTED from the origin preset, never assumed to still hold it. The fade writes DENSE tween bags into `live` every frame (see `patchLiveObject`'s write-rule note), so keys the sparse origin omits are left stranded; `selectPresetChanged` diffs on key existence and the origin then reads dirty.
 */
export const abortTransition = () => (dispatch, getState) => {
	const s = getState()
	// ⚠️ A DETACHED fade departed from a mid-fade snapshot, so `current_preset_index` names a preset it never left. Re-asserting that preset snaps `live` to a state the user never saw.
	const snapshot = s.playback.transition_detached ? transitionManager.fromState({ terminal: true }) : null
	const origin = snapshot ? null : selectPlaylist(s)[s.playback.current_preset_index]
	// eslint-disable-next-line no-unused-vars
	const { playback: unused, ...rawSettings } = snapshot ?? origin?.settings ?? {}
	if (snapshot) holdMorphables(rawSettings, selectLive(s))
	// Read BEFORE kill(), which nulls the snapshot with the rest of the timeline's state.
	const objects = snapshot ? (snapshot.objects ?? null) : (origin?.settings?.objects ?? null)
	transitionManager.kill(objects)
	if (snapshot || origin) dispatch(settleLive({ settings: rawSettings }))
	dispatch(setNextPresetIndex(-1))
	// The fade was what the playlist was waiting on. Abandoning it settles nothing through the timeline's own callbacks, so this is the only place the run can be re-planned — without it autoplay stays on and never advances again.
	startRun(dispatch, getState)
}

/** The settle's half of the end snap; `progressAt` reads the same constant for the paint. */
const SETTLE_EPSILON = SCRUB_END_EPSILON

/**
 * End a scrub: settle the fade if it was released at either end, otherwise leave it paused where it is.
 *
 * ⚠️ The decision reads LIVE state rather than taking the position as an argument. The release handler closes over the render it started in, where progress was still 0, so a caller passing what it can see would always settle the wrong way — or not at all.
 */
export const releaseScrub =
	(openedIndex = -1) =>
	(dispatch, getState) => {
		const { next_preset_index } = selectPlayback(getState())
		if (next_preset_index < 0) return
		const t = transitionManager.getProgress()
		// ⚠️ A peer's `loadPreset` retargets the running fade mid-drag — `sync` is only refused while autoplay plays, and a scrub has stopped it. A release settles only the fade it opened.
		if (openedIndex >= 0 && openedIndex !== next_preset_index) return
		if (t >= 1 - SETTLE_EPSILON) transitionManager.complete()
		else if (t <= SETTLE_EPSILON) dispatch(abortTransition())
		// ⚠️ Parked between the ends fires no callback: without this commit Redux keeps pre-scrub values, so the dirty dot and a save read stale. A parked run would stall autoplay while it reads playing.
		else {
			dispatch(commitLiveFromTransition())
			dispatch(stopPlayback())
		}
	}

/** Let a released scrub run on at the fade's own rate: `forward` to the destination, else back to the origin. Only the fade the gesture opened. */
export const playOutScrub = (openedIndex, forward) => (dispatch, getState) => {
	if (!transitionManager.isLive() || selectPlayback(getState()).next_preset_index !== openedIndex) return
	if (forward) transitionManager.complete()
	else transitionManager.reverse(() => dispatch(abortTransition()))
}

/**
 * Move the running fade to `t` (0–1), leaving it paused there.
 *
 * ⚠️ Gated on a fade being IN FLIGHT, never on `timeline` existing — a completed fade leaves one parked at progress 1, and `loadPreset` awaits `preloadTextures` before replacing it. Ungated, the opening drag seeks into that gap and rewinds the PREVIOUS fade.
 */
export const scrubTransition = t => (dispatch, getState) => {
	if (selectPlayback(getState()).next_preset_index < 0) return
	transitionManager.seek(t)
}

/**
 * Freeze the running fade, or — when already frozen — play it out to whichever end `index` names: the destination runs forward, the origin reverses and abandons the destination. An unnamed index runs forward, the way an unpaused fade would have gone on its own.
 *
 * Forward always goes through `complete()`: a timeline scrubbed to `progress === 1` has no time left to play, so a plain resume would leave it live forever with nothing to fire its completion.
 */
export const toggleTransitionPaused = index => (dispatch, getState) => {
	if (!transitionManager.isLive()) return
	const { transition_paused, current_preset_index } = selectPlayback(getState())
	if (!transition_paused) {
		transitionManager.pause()
		return
	}
	if (index === current_preset_index) transitionManager.reverse(() => dispatch(abortTransition()))
	else transitionManager.complete()
}

/**
 * Freeze or unfreeze the running fade from a control that names NO cell — the playing pie, which sits outside the grid and so has no end to aim at.
 *
 * ⚠️ Unfreezing carries on the way the fade was ALREADY going, which is the timeline's sticky direction and not `transition_reversed` — that flag records the gesture that opened the fade, and a fade a cell-click reversed still carries the flag of the drag that opened it forward. Reading it would send a reversing fade back at its destination.
 *
 * ⚠️ Neither end is a plain `resume()`: a fade scrubbed to an end has no time left to play, so the settle would never fire. Forward goes through `complete()` and backward through `reverse()`, exactly as the cell clicks do.
 */
export const togglePlayingTransition = () => dispatch => {
	if (!transitionManager.isLive()) return
	if (transitionManager.isPaused()) dispatch(resumePlayingTransition())
	else transitionManager.pause()
}

/** Unfreeze a frozen fade the way `togglePlayingTransition` does, for a caller that has already decided the direction is "onward" rather than toggling — the play button. A no-op unless a fade is live and frozen. */
export const resumePlayingTransition = () => dispatch => {
	if (!transitionManager.isLive() || !transitionManager.isPaused()) return
	if (transitionManager.isReversed()) transitionManager.reverse(() => dispatch(abortTransition()))
	else transitionManager.complete()
}

/**
 * Start a fade into `index` that the user drives by hand, parked at progress 0.
 *
 * A user taking the transition over stops autoplay, exactly as a preset click does — `loadPreset` already does that for any load without `advance`.
 */
export const startScrubTransition =
	(index, back = false) =>
	dispatch => {
		// ⚠️ AFTER the load, and only if a fade OPENED: `setNextPresetIndex` clears `transition_reversed`, and a refused load has no `next_preset_index` to ever clear a stamp — it would paint the next fade on the wrong edge.
		const opened = dispatch(loadPreset({ index, scrub: true, confirmed: true, select: true }))
		if (back && opened) dispatch(setTransitionReversed(true))
	}

export const loadNextPreset = (instant, confirmed) => (dispatch, getState) => {
	const { current_preset_index, next_preset_index } = selectPlayback(getState())
	const base = next_preset_index >= 0 ? next_preset_index : current_preset_index
	dispatch(loadPreset({ index: base + 1, instant, confirmed, select: true }))
}

export const loadPrevPreset = (instant, confirmed) => (dispatch, getState) => {
	const { current_preset_index, next_preset_index } = selectPlayback(getState())
	const base = next_preset_index >= 0 ? next_preset_index : current_preset_index
	dispatch(loadPreset({ index: base - 1, instant, confirmed, select: true }))
}

// Autoplay only — a running fade is NOT touched. The transition is controlled on its own, so a fade started by a preset click keeps running when autoplay stops, and one already in flight finishes.
//
// The playhead IS the elapsed record, so pausing banks nothing.
export const stopPlayback = () => dispatch => {
	logger.info('stopPlayback')
	awaitingWarm = false
	dispatch(setPlaying(false))
	dropRun(dispatch)
}

/** This desktop lost the runner role: its run ends, and `playing` stays the session's, for the new runner to carry on. */
export const yieldPlayback = () => dispatch => {
	awaitingWarm = false
	if (run) dropRun(dispatch)
}

/** Textures landed: carry out the playing session `startPlayback` could not arm while they warmed. */
export const armWhenWarm = () => (dispatch, getState) => {
	if (!awaitingWarm) return
	awaitingWarm = false
	const s = getState()
	if (selectPlaying(s) && selectIsRunner(s)) dispatch(startPlayback())
}

// ⚠️ Never starts a second run or rebuilds a parked child: a fade in flight is left as it is and the plan is built behind it.
export const startPlayback = () => (dispatch, getState) => {
	logger.info('startPlayback')
	const s = getState()
	const { current_preset_index } = selectPlayback(s)
	const presets = selectPlaylist(s)

	const adopting = selectPlaying(s) && selectIsRunner(s)

	if (presets.length < 2) {
		logger.warn('startPlayback: not enough presets')
		// A session another runner left playing: nothing here can run it, so it stops for everyone.
		if (adopting) dispatch(stopPlayback())
		return
	}

	awaitingWarm = adopting && !selectTexturesReady(s)
	if (!selectTexturesReady(s)) {
		logger.warn('startPlayback: textures still loading')
		return
	}

	// Anywhere but the runner, play is a request: the flag reaches the runner, which runs it.
	if (!selectIsRunner(s)) {
		dispatch(setPlaying(true))
		return
	}

	if (current_preset_index === -1) dispatch(loadPreset({ index: 0 }))

	dispatch(setPlaying(true))

	// Never `isLive`, true forever after any landed fade: a standalone fade still MOVING re-plans from its own settle.
	if (transitionManager.isActive() && !transitionManager.isRun()) return

	startRun(dispatch, getState)
}

/**
 * The play/pause button (and the `toggle_playback` key) drives autoplay AND the running fade in the same press, so a transport that reads "paused" cannot leave something still moving and one that reads "playing" cannot leave something frozen.
 *
 * - **Pause** additionally freezes a fade in flight.
 * - **Play** additionally unfreezes a frozen fade, onward the way it was already going (`resumePlayingTransition`).
 *
 * ⚠️ The autoplay half runs unconditionally either way — a fade is never a reason to skip it. `startPlayback` yields to a standalone fade in flight and that fade's own settle re-plans, so play over a running fade still arms autoplay for when it lands.
 */
export const togglePlayback = () => (dispatch, getState) => {
	if (selectPlaying(getState())) {
		// ⚠️ FREEZE FIRST, then stop. `stopPlayback` drops the plan through `killRun`, and a fade at the playhead dies WITH it — the press would reset the fade to its origin instead of holding it where the user stopped it. `pause()` hands the child off the plan before anything can kill it; the run it parks is then dropped by the `stopPlayback` below, which is what the press means.
		if (transitionManager.isActive()) transitionManager.pause()
		dispatch(stopPlayback())
	} else {
		dispatch(resumePlayingTransition())
		dispatch(startPlayback())
	}
}

// Register loadPreset in action registry for confirmation dialogs Done here to avoid circular import issues
registerAction('loadPreset', params => loadPreset(params))

// Dev only — the render-cost harness (tests/manual/clockBench.js) needs to run the playlist the way the app does. Driving it with a hand-rolled `loadPreset` loop instead measures a fade cadence no user ever produces: the hold timer, its re-arm on settle, and the `advance` flag that keeps autoplay alive are exactly what a boundary costs. Never in production: this lets any page script take the playlist over.
if (IS_LOCAL && typeof window !== 'undefined') {
	window.startPlayback = startPlayback
	window.stopPlayback = stopPlayback
	window.togglePlayback = togglePlayback
	window.scrubTransition = scrubTransition
	window.releaseScrub = releaseScrub
	window.toggleTransitionPaused = toggleTransitionPaused
	window.abortTransition = abortTransition
}
